import React, { useEffect, useMemo, useState } from "react";
import InputField from "components/Input/InputField";
import TextArea from "components/Input/TextArea";
import {
  Autocomplete,
  Button,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
} from "@mui/material";
import { toast } from "react-toastify";
import axios from "axios";
import { useMutation } from "@tanstack/react-query";
import { useLocation, useParams } from "react-router-dom";
import { API_URL_PUBLIC } from "const/constants";
import { fetchCheckInFormConfigBySlug, getDefaultCheckInFormConfig } from "utils/checkInFormConfig";

export default function CheckInForm() {
  const initialState = {
    name: "",
    number: "",
    email: "",

    address: "",

    emergencyContactName: "",
    emergencyContact: "",
    visaStatus: "",
    visaExpiry: "",

    interestedServices: [],
    other: "",
    source: "",
    sourceOther: "",
    comments: "",
    type: "check-in",

    clientConversionStatus: false,
    followUpDate: "",
    summary: "",
    discussion: "",
    note: "",

    gender: "",
    dob: "",
    maritalStatus: "",
    usiNumber: "",

    passportNumber: "",
    dateOfIssue: "",
    passportExpiry: "",
    nationality: "",
    status: "",

    emergencyRelation: "",
    emergencyEmail: "",
  };
  const [form, setForm] = useState(initialState);
  const [fieldConfig, setFieldConfig] = useState(getDefaultCheckInFormConfig);
  const [configLoading, setConfigLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const enabledFields = useMemo(() => fieldConfig.filter((field) => field.enabled), [fieldConfig]);
  const { slug } = useParams();
  const location = useLocation();
  const token = location?.pathname?.split("/")[3];

  useEffect(() => {
    if (slug) {
      fetchCheckInFormConfigBySlug(slug).then(({ fields }) => {
        setFieldConfig(fields);
        setConfigLoading(false);
      });
    } else {
      setConfigLoading(false);
    }
  }, [slug]);
  const { mutate, isLoading } = useMutation({
    mutationFn: (form) => {
      return axios.post(`${API_URL_PUBLIC}/public/client-form/${slug}`, form, {
        headers: {
          Authorization: null,
        },
      });
    },
    onSuccess: () => {
      setForm(initialState);
      setSubmitted(true);
    },
    onError: (error) => {
      if (error?.response?.status === 429) {
        setSubmitError(
          error?.response?.data?.errorMessage ||
            "Too many submissions. Please wait 10 minutes before trying again."
        );
      } else {
        setSubmitError("Form submission failed. Please try again.");
      }
    },
  });
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleServiceChange = (e) => {
    const { name, value, checked } = e.target;
    setForm((prev) => {
      const current = prev[name] || [];
      const updated = checked ? [...current, value] : current.filter((s) => s !== value);
      return { ...prev, [name]: updated };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!token) {
      setSubmitError("Token is required");
      return;
    }
    setSubmitError("");
    const fieldById = new Map(fieldConfig.map((f) => [f.id, f]));
    const payload = {};
    Object.entries(form).forEach(([id, value]) => {
      const field = fieldById.get(id);
      payload[field?.isCustom ? field.label : id] = value;
    });
    mutate(payload);
  };

  if (isLoading || configLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-app-surface-alt via-app-surface-muted to-app-surface-muted flex items-center justify-center py-8 px-4">
      <div className="w-full max-w-5xl bg-app-surface rounded-xl shadow-2xl  border-white/20 backdrop-blur-sm overflow-hidden">
        {/* Modern Header */}
        <div className="relative bg-primary px-4 md:px-8 py-4 md:py-8">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="relative flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex flex-col md:flex-row items-center gap-4">
              <div className="">
                <img
                  src={process.env.PUBLIC_URL + "/logo.png"}
                  alt="Logo"
                  className="w-32 object-contain"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://via.placeholder.com/48x48?text=Logo";
                  }}
                />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white mb-1">Office Check-in</h1>
                <p className="text-blue-100 text-sm font-medium">Kothar Educational Services</p>
              </div>
            </div>
          </div>
        </div>

        {submitted ? (
          <div className="flex flex-col items-center justify-center py-20 px-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
              <svg
                className="w-10 h-10 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-app-text mb-2">Check-in Successful!</h2>
            <p className="text-app-text-secondary mb-2">
              Thank you. Your check-in has been recorded.
            </p>
            <p className="text-app-text-muted text-sm">You may close this tab now.</p>
            {/* <button
              onClick={() => setSubmitted(false)}
              className="px-8 py-3 bg-primary text-white font-semibold rounded-xl hover:opacity-90 transition-opacity mt-6"
            >
              Check In Again
            </button> */}
          </div>
        ) : (
          <form className="p-8 space-y-8" onSubmit={handleSubmit}>
            <div className="bg-gradient-to-br from-app-surface-alt to-app-surface-muted rounded-2xl p-4 md:p-8 border border-app-border/50 shadow-lg">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <span className="text-primary font-semibold">1</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-app-text">Check-in Details</h3>
                  <p className="text-sm text-app-text-secondary">
                    Please fill out the fields below.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {enabledFields.map((field) => {
                  const isFullWidth = field.layout === "full";
                  const wrapperClass = isFullWidth ? "md:col-span-2" : "";

                  if (field.type === "textarea") {
                    return (
                      <div key={field.id} className={wrapperClass}>
                        <TextArea
                          label={field.label}
                          name={field.id}
                          value={form[field.id]}
                          onChange={handleChange}
                          rows={4}
                          required={field.required}
                        />
                      </div>
                    );
                  }

                  if (field.type === "checkbox-group") {
                    const fieldOptions = field.options || [];
                    return (
                      <div key={field.id} className={wrapperClass}>
                        <label className="block uppercase text-app-text-secondary text-xs font-semibold mb-2">
                          {field.label} {field.required && <sup className="text-red-500">*</sup>}
                        </label>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                          {fieldOptions.map((option) => (
                            <label
                              key={option}
                              className="flex items-center gap-3 hover:border-blue-300 hover:shadow-md cursor-pointer transition-all duration-200 group"
                            >
                              <input
                                type="checkbox"
                                name={field.id}
                                value={option}
                                checked={(form[field.id] || []).includes(option)}
                                onChange={handleServiceChange}
                                className="text-primary bg-app-surface-muted border-app-border rounded focus:ring-blue-500 focus:ring-2 transition-all duration-200"
                              />
                              <span className="text-app-text font-medium group-hover:text-primary transition-colors duration-200 text-sm">
                                {option}
                              </span>
                            </label>
                          ))}
                        </div>
                        {(form[field.id] || []).includes("Other") && (
                          <InputField
                            label="Mention Other"
                            name="other"
                            value={form.other}
                            onChange={handleChange}
                          />
                        )}
                      </div>
                    );
                  }

                  if (field.type === "select") {
                    const fieldOptions = field.options || [];
                    return (
                      <div key={field.id} className={wrapperClass}>
                        <FormControl fullWidth size="small" required={field.required}>
                          <InputLabel>{field.label}</InputLabel>
                          <Select
                            name={field.id}
                            value={form[field.id] || ""}
                            label={field.label}
                            onChange={handleChange}
                            inputProps={{ required: field.required }}
                          >
                            {fieldOptions.map((opt) => (
                              <MenuItem key={opt} value={opt}>
                                {opt}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </div>
                    );
                  }

                  if (field.type === "autocomplete") {
                    const rawOptions = field.options || [];
                    const options = rawOptions.map((o) => ({ label: o, value: o }));
                    const selectedOption =
                      options.find((option) => option.value === form[field.id]) || null;

                    return (
                      <div key={field.id} className={wrapperClass}>
                        <Autocomplete
                          options={options}
                          value={selectedOption}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={field.label}
                              placeholder="Example: Google, Facebook, Tiktok, Instagram, Family or Friends."
                              required={field.required}
                            />
                          )}
                          size="small"
                          onChange={(e, value) => {
                            handleChange({
                              target: { name: field.id, value: value?.value },
                            });
                          }}
                          isOptionEqualToValue={(option, value) => option.value === value?.value}
                        />
                        {form[field.id] === "Other" && (
                          <div className="mt-3">
                            <InputField
                              label="Please specify"
                              name="sourceOther"
                              value={form.sourceOther}
                              onChange={handleChange}
                            />
                          </div>
                        )}
                      </div>
                    );
                  }

                  return (
                    <div key={field.id} className={wrapperClass}>
                      <InputField
                        label={field.label}
                        name={field.id}
                        value={form[field.id]}
                        onChange={handleChange}
                        required={field.required}
                        type={field.type}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
            {submitError && (
              <p className="mt-4 text-sm text-red-600 text-center font-medium">{submitError}</p>
            )}
            <div className="pt-6 w-full">
              <Button type="submit" variant="contained" disabled={isLoading} className="w-full">
                Submit Form
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
